import { Injectable, inject, WritableSignal } from '@angular/core';
import { Subscription } from 'rxjs';
import { MapService, ShowOnMapEnum } from './map.service';
import { WaypointService } from '../core/waypoint.service';
import { StackedModalsService } from '../modal/stacked-modals.service';
import { MapRouteDrawerService } from './map-route-drawer.service';
import { MapComponent } from '../../components/map/map.component';
import { MapElementType } from '../../models/map-element-type';
import { DisplayMode } from '../../models/display-mode';
import { VehicleOptionsDialogComponent } from '../../components/modals/vehicle-options-dialog/vehicle-options-dialog.component';
import { SettingsDialogComponent } from '../../components/modals/settings-dialog/settings-dialog.component';
import { HistoryDialogComponent } from '../../components/modals/history-dialog/history-dialog.component';

/**
 * MainComponent implements this and passes itself to bind().
 */
export interface MapUICoordinatorHost {
  readonly mapComponent?: MapComponent;
  readonly iframeRedirect: WritableSignal<boolean>;
  readonly showOnMap: WritableSignal<{ type: MapElementType | ShowOnMapEnum; data?: any } | null>;
  readonly modeBeforeHistory: WritableSignal<DisplayMode | null>;
}

/**
 * Owns the two map-driven UI subscriptions that were living in MainComponent:
 *   - ObservableShowOnMap     (address, charger, history, settings back-nav)
 *   - ObservableDrawTripToMap (trip-history playback)
 */
@Injectable({ providedIn: 'root' })
export class MapUICoordinator {
  private host: MapUICoordinatorHost | null = null;
  private showOnMapSub: Subscription | null = null;
  private drawTripSub: Subscription | null = null;
  private followModeRangeSub: Subscription | null = null;
  private followModeStateSub: Subscription | null = null;

  private readonly mapService = inject(MapService);
  private readonly waypointService = inject(WaypointService);
  private readonly stackedModalsService = inject(StackedModalsService);
  private readonly mapRouteDrawerService = inject(MapRouteDrawerService);

  bind(host: MapUICoordinatorHost): void {
    this.host = host;

    if (!this.showOnMapSub) {
      this.showOnMapSub = this.mapService.ObservableShowOnMap.subscribe(resp => {
        if (!this.host || resp == null) return;

        if (resp.type === ShowOnMapEnum.BackSettings) {
          this.host.showOnMap.set(null);
          this.stackedModalsService.openModal(SettingsDialogComponent, {
            panelClass: 'dialog-snap-to-fullscreen',
            disableClose: true,
            autoFocus: false,
            data: resp.data ?? undefined,
          });
          return;
        }

        if (resp.type === ShowOnMapEnum.BackHistory) {
          this.host.showOnMap.set(null);
          this.mapRouteDrawerService.clearTrip();
          this.waypointService.setSelectedDisplayMode(this.host.modeBeforeHistory() ?? DisplayMode.RANGE);
          this.host.modeBeforeHistory.set(null);
          this.stackedModalsService.openModal(HistoryDialogComponent, {
            panelClass: 'dialog-snap-to-fullscreen',
            disableClose: true,
            autoFocus: false,
            data: resp.data,
          });
          return;
        }

        if (resp.data && resp.type === ShowOnMapEnum.BackVehicleOptions) {
          this.host.showOnMap.set(null);
          this.stackedModalsService.openModal(VehicleOptionsDialogComponent, {
            autoFocus: false,
            panelClass: 'dialog-snap-to-fullscreen',
          });
          return;
        }

        if (
          (resp.data && resp.type === MapElementType.Address) ||
          resp.type === MapElementType.FavoriteChargingStation ||
          resp.type === MapElementType.DisabledChargingStation ||
          (resp.data && resp.type === MapElementType.RouteHistory)
        ) {
          this.host.showOnMap.set(resp);
        }
      });
    }

    if (!this.drawTripSub) {
      this.drawTripSub = this.mapService.ObservableDrawTripToMap.subscribe(tripPoints => {
        if (!this.host || !tripPoints?.length) return;
        this.host.modeBeforeHistory.set(this.waypointService.getSelectedDisplayMode());
        this.waypointService.setSelectedDisplayMode(DisplayMode.SHOW_ON_MAP);
        if (this.host.mapComponent) {
          this.host.mapComponent.clearMapLayers();
        }
        this.mapRouteDrawerService.drawTripToMap(tripPoints);
      });
    }

    // A searched start point pins the range origin; a GPS "go to my location"
    // re-enables following. Without this the map never learns about the pin
    // and keeps re-anchoring the range to the moving GPS fix.
    if (!this.followModeRangeSub) {
      this.followModeRangeSub = this.mapService.ObservableSetFollowModeRange.subscribe(enabled => {
        if (!this.host?.mapComponent || enabled == null) return;
        this.host.mapComponent.setFollowModeRange(enabled);
      });
    }

    // Camera jumps requested through MapService (centerMap on a search result
    // or saved address) must leave follow mode, or the follow camera snaps the
    // view back onto the cursor. MapComponent owns the state, so forward it.
    if (!this.followModeStateSub) {
      this.followModeStateSub = this.mapService.followModeStateRequested$.subscribe(state => {
        this.host?.mapComponent?.setFollowModeState(state);
      });
    }
  }

  unbind(): void {
    this.showOnMapSub?.unsubscribe();
    this.showOnMapSub = null;
    this.drawTripSub?.unsubscribe();
    this.drawTripSub = null;
    this.followModeRangeSub?.unsubscribe();
    this.followModeRangeSub = null;
    this.followModeStateSub?.unsubscribe();
    this.followModeStateSub = null;
    this.host = null;
  }
}
